// Import React directly first to ensure it's loaded before createContext is used
import * as React from 'react';

export type ModeType = 'regular' | 'travel' | 'sick' | 'caregiver';

export interface ModeContextType {
  currentMode: ModeType;
  setMode: (mode: ModeType) => void;
  connectToHealthKit: () => Promise<boolean>;
  expandedSection: string | null;
  setExpandedSection: (section: string | null) => void;
}

// We don't initialize the context at module evaluation time
let ModeContext: React.Context<ModeContextType>;

// This function will only be called when the context is actually used
const getModeContext = (): React.Context<ModeContextType> => {
  if (!ModeContext) {
    ModeContext = React.createContext<ModeContextType>({
      currentMode: 'regular',
      setMode: () => {},
      connectToHealthKit: async () => false,
      expandedSection: null,
      setExpandedSection: () => {},
    });
  }
  return ModeContext;
};

export const ModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get the context only when the component is rendered
  const Context = getModeContext();
  const [currentMode, setCurrentMode] = React.useState<ModeType>('regular');
  const [expandedSection, setExpandedSection] = React.useState<string | null>(null);

  const connectToHealthKit = async (): Promise<boolean> => {
    // HealthKit integration is disabled
    
    return false;
  };

  const setMode = (mode: ModeType) => {
    setCurrentMode(mode);
  };

  return (
    <Context.Provider 
      value={{ 
        currentMode, 
        setMode, 
        connectToHealthKit, 
        expandedSection,
        setExpandedSection
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useMode = () => {
  // Get the context only when the hook is called
  const Context = getModeContext();
  return React.useContext(Context);
};